import { Button, CardButton } from "../../../design-system";
import React from "react";
import { AlbumData } from "../Playlist.types";

type MenuItem = {
  icon: React.ReactNode;
  label: string;
};

type AlbumSelectionPageProps = {
  albums: AlbumData[];
  isLoading: boolean;
  errorMessage: string;
  sidebarItems: MenuItem[];
  bottomTabs: MenuItem[];
  activeMenuLabel: string;
  onMenuAction: (label: string) => void;
  onSelectAlbum: (albumId: string) => void;
};

function AlbumSelectionPage({
  albums,
  isLoading,
  errorMessage,
  sidebarItems,
  bottomTabs,
  activeMenuLabel,
  onMenuAction,
  onSelectAlbum,
}: AlbumSelectionPageProps) {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-player text-white flex items-center justify-center">
        <p className="text-player-secondary">Carregando álbuns...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-player text-white">
      <div className="hidden md:flex min-h-screen">
        <aside className="w-24 border-r border-player-surface px-3 py-6">
          <nav className="space-y-7">
            {sidebarItems.map((item) => (
              <Button variant="ghost" size="none"
                key={item.label}
                onClick={() => onMenuAction(item.label)}
                className={`w-full flex flex-col items-center ${item.label === activeMenuLabel
                  ? "text-white"
                  : "text-player-secondary hover:text-white"
                  }`}
              >
                {item.icon}
                <span className="mt-2 text-xs">{item.label}</span>
              </Button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 px-8 pt-8 pb-10 bg-gradient-to-b from-player-surface via-player to-player">
          <div className="max-w-5xl mx-auto">

            <h1 className="text-3xl md:text-4xl font-semibold">Music:</h1>

            {errorMessage ? (
              <p className="text-player-muted mt-4">{errorMessage}</p>
            ) : null}

            {!errorMessage && !albums.length ? (
              <p className="text-player-muted mt-4">Nenhum álbum encontrado.</p>
            ) : null}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {albums.map((album) => (
                <CardButton
                  key={album.id}
                  onClick={() => onSelectAlbum(album.id)}
                >
                  <img
                    src={album.coverUrl || "images/album_over_top.jpg"}
                    alt={album.title}
                    className="w-full aspect-square object-cover"
                    onError={(event) => {
                      event.currentTarget.src = "images/album_inner.jpg";
                    }}
                  />
                  <div className="p-4">
                    <p className="text-xl font-semibold truncate">
                      {album.title}
                    </p>
                    <p className="text-player-muted truncate">
                      {album.artistName || "Artista"}
                      {album.year ? ` • ${album.year}` : ""}
                    </p>
                  </div>
                </CardButton>
              ))}
            </div>
          </div>
        </main>
      </div>

      <div className="md:hidden min-h-screen px-6 py-10 pb-28 bg-gradient-to-b from-player-surface via-player to-player">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-semibold">Music:</h1>

          {errorMessage ? (
            <p className="text-player-muted mt-4">{errorMessage}</p>
          ) : null}

          {!errorMessage && !albums.length ? (
            <p className="text-player-muted mt-4">Nenhum álbum encontrado.</p>
          ) : null}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {albums.map((album) => (
              <CardButton
                key={`${album.id}-mobile`}
                onClick={() => onSelectAlbum(album.id)}
              >
                <img
                  src={album.coverUrl || "images/album_over_top.jpg"}
                  alt={album.title}
                  className="w-full aspect-square object-cover"
                  onError={(event) => {
                    event.currentTarget.src = "images/album_inner.jpg";
                  }}
                />
                <div className="p-4">
                  <p className="text-xl font-semibold truncate">
                    {album.title}
                  </p>
                  <p className="text-player-muted truncate">
                    {album.artistName || "Artista"}
                    {album.year ? ` • ${album.year}` : ""}
                  </p>
                </div>
              </CardButton>
            ))}
          </div>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 h-20 bg-player-bottom border-t border-player-raised px-6 flex items-center justify-between">
        {bottomTabs.map((tab) => (
          <Button variant="ghost" size="none"
            key={tab.label}
            onClick={() => onMenuAction(tab.label)}
            className={`flex flex-col items-center ${tab.label === activeMenuLabel ? "text-white" : "text-control-hover"
              }`}
          >
            {tab.icon}
            <span className="text-xs mt-1">{tab.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

export default AlbumSelectionPage;
